/**
 * The map switcher — `docs/property-maps-v2.md` §5.6, board 5.
 *
 * Which map is open, what else there is, and the two ways to start another.
 * **Archived maps are behind an expander, not gone**: an archived map is
 * still readable and still restorable (rule 2), and hiding it entirely would
 * make somebody re-draw a plan that already exists.
 */
import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Button } from '../../components/primitives.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { IconCheck, IconMaps, IconPlus, IconRuler } from '../../frame/icons.js'
import type { MapSummary } from '../api.js'
import { layerWords } from '../words.js'

export function MapSwitcher({
  maps, openId, canEdit, offerMeasure, onOpen, onNew, onMeasure, onClose,
}: {
  maps: MapSummary[]
  openId: string | null
  canEdit: boolean
  offerMeasure: boolean
  onOpen: (mapId: string) => void
  onNew: () => void
  onMeasure: () => void
  onClose: () => void
}) {
  const [showArchived, setShowArchived] = useState(false)
  const live = maps.filter((one) => !one.archived)
  const archived = maps.filter((one) => one.archived)

  return (
    <SheetOrDialog onClose={onClose} title="Maps on this property" width={460}>
      <div className="flex flex-col px-2 py-2">
        {live.map((one) => (
          <Row key={one.id} map={one} open={one.id === openId} onOpen={() => onOpen(one.id)} />
        ))}

        {canEdit && (
          <div className="flex flex-col gap-1 border-t border-line-faint px-1.5 pt-2">
            <Button className="justify-start" onClick={onNew}>
              <IconPlus size={13} />
              New map…
            </Button>
            {offerMeasure && (
              <Button className="justify-start" onClick={onMeasure}>
                <IconRuler size={13} />
                Just measure…
                <span className="ml-auto text-text-faint">no map saved</span>
              </Button>
            )}
          </div>
        )}

        {archived.length > 0 && (
          <div className="border-t border-line-faint px-1.5 pt-2">
            <button
              type="button"
              onClick={() => setShowArchived((was) => !was)}
              className="w-full px-2 py-1.5 text-left text-cell text-text-muted hover:text-text"
            >
              Archived · {archived.length}
            </button>
            {showArchived && archived.map((one) => (
              <Row key={one.id} map={one} open={false} onOpen={() => onOpen(one.id)} />
            ))}
          </div>
        )}
      </div>
    </SheetOrDialog>
  )
}

function Row({ map, open, onOpen }: { map: MapSummary; open: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex items-center gap-3 rounded-field px-3 py-2.5 text-left hover:bg-hover',
        open && 'bg-accent-soft',
      )}
    >
      <span className={cn('shrink-0', open ? 'text-accent' : 'text-text-faint')}>
        <IconMaps size={16} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <b className="truncate text-body">{map.name}</b>
        <span className="truncate text-sub text-text-muted">
          {layerWords(map.layerCount)}
          {map.audienceSummary.length > 0 && ` · ${map.audienceSummary[0]}`}
        </span>
      </span>
      {open && <span className="shrink-0 text-accent"><IconCheck size={14} /></span>}
    </button>
  )
}
